import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Headphones, Star, Sparkles, ChevronRight, ArrowRight } from 'lucide-react';
import { db, collection, addDoc } from '../services/firebase';

const STORAGE_KEY = 'lg_welcome_done';

export default function WelcomePopup() {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);
  const [rating, setRating] = useState(0);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  const user = JSON.parse(localStorage.getItem('lg_user') || '{}');

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    if (!user.phone) return;
    // Check Firebase if welcome already shown
    (async () => {
      try {
        const { getDoc } = await import('firebase/firestore');
        const { doc: fbDoc } = await import('../services/firebase');
        const snap = await getDoc(fbDoc(db, 'users', user.phone));
        if (snap.exists() && snap.data().welcomeDone) {
          localStorage.setItem(STORAGE_KEY, 'true');
          return;
        }
      } catch (e) {}
      const timer = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(timer);
    })();
  }, []);

  const markDone = async () => {
    setShow(false);
    localStorage.setItem(STORAGE_KEY, 'true');
    // Save to Firebase so it never shows again even after cache clear
    try {
      const { updateDoc } = await import('firebase/firestore');
      const { doc: fbDoc } = await import('../services/firebase');
      if (user.phone) await updateDoc(fbDoc(db, 'users', user.phone), { welcomeDone: true });
    } catch (e) {}
  };

  const next = () => {
    if (step < 2) setStep(s => s + 1);
    else markDone();
  };

  const submitRating = async () => {
    if (rating === 0) return;
    try {
      // Prevent duplicate
      const { getDocs, query, where } = await import('firebase/firestore');
      const q = query(collection(db, 'app_ratings'), where('phone', '==', user.phone || ''));
      const existing = await getDocs(q);
      if (!existing.empty) { setRatingSubmitted(true); setTimeout(markDone, 1500); return; }
      await addDoc(collection(db, 'app_ratings'), {
        phone: user.phone || '',
        name: user.name || '',
        rating,
        createdAt: new Date(),
        device: navigator.userAgent?.slice(0, 100) || '',
      });
    } catch (err) {}
    setRatingSubmitted(true);
    setTimeout(markDone, 1500);
  };

  if (!show) return null;

  const slides = [
    {
      icon: Rocket,
      gradient: 'from-[#1e3a5f] to-[#2563eb]',
      iconBg: 'bg-[#1e3a5f]/10 dark:bg-[#1e3a5f]/30',
      title: 'Install the App 🚀',
      desc: 'Add Lucy Garden to your Home Screen for a fast, app-like experience. Quick 1-tap ordering.',
      action: 'Install Now',
      onAction: async () => {
        if (window.__lgInstallPrompt) {
          window.__lgInstallPrompt.prompt();
          await window.__lgInstallPrompt.userChoice;
          window.__lgInstallPrompt = null;
        }
        next();
      },
      skip: 'Not now',
    },
    {
      icon: Headphones,
      gradient: 'from-amber-600 to-orange-600',
      iconBg: 'bg-amber-500/15 dark:bg-amber-500/25',
      title: 'Need Help or Have Questions? 🎧',
      desc: 'Our wholesale support desk and instant FAQs are always ready to assist your store operations.',
      action: 'Visit Support Desk',
      onAction: () => {
        markDone();
        window.location.href = '/support';
      },
      skip: "I'll explore myself",
    },
    {
      icon: Star,
      gradient: 'from-indigo-600 to-blue-600',
      iconBg: 'bg-indigo-500/15 dark:bg-indigo-500/25',
      title: 'Rate Lucy Garden ⭐',
      desc: 'Your rating helps us improve and serve fresh dairy to you reliably.',
      isRating: true,
      skip: 'Maybe later',
    },
  ];

  const current = slides[step];

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.92, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 24, stiffness: 260 }}
          className="bg-white dark:bg-[#161514] rounded-2xl w-full max-w-[340px] overflow-hidden shadow-2xl border border-[#e2e0db] dark:border-[#2e2d2b] relative">

          {/* Top gradient bar */}
          <div className={`h-1.5 bg-gradient-to-r ${current.gradient}`} />

          {/* Content */}
          <div className="relative px-6 pt-7 pb-6">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-1.5 mb-5">
              {[0, 1, 2].map(i => (
                <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? `w-6 bg-gradient-to-r ${current.gradient}` : i < step ? 'w-2 bg-emerald-500' : 'w-2 bg-[#e2e0db] dark:bg-[#2e2d2b]'}`} />
              ))}
            </div>

            {/* Icon */}
            <motion.div key={step} initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', delay: 0.1 }}
              className={`w-14 h-14 ${current.iconBg} rounded-xl flex items-center justify-center mx-auto mb-4 border border-black/5 dark:border-white/10`}>
              <current.icon size={26} style={{ color: step === 0 ? '#1e3a5f' : step === 1 ? '#d97706' : '#4f46e5' }} />
            </motion.div>

            {/* Title */}
            <motion.h2 key={`t-${step}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="text-xl font-black text-gray-800 dark:text-white text-center">
              {current.title}
            </motion.h2>

            {/* Description */}
            <motion.p key={`d-${step}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
              className="text-sm text-gray-500 dark:text-gray-400 text-center mt-3 leading-relaxed">
              {current.desc}
            </motion.p>

            {/* Rating Stars */}
            {current.isRating && !ratingSubmitted && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="flex items-center justify-center gap-2.5 mt-5">
                {[1, 2, 3, 4, 5].map(i => (
                  <motion.button key={i} whileTap={{ scale: 0.8 }}
                    onClick={() => setRating(i)}
                    className="p-0.5">
                    <Star size={34} fill={i <= rating ? '#f59e0b' : 'none'}
                      className={`transition-colors ${i <= rating ? 'text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
                      strokeWidth={i <= rating ? 0 : 1.5} />
                  </motion.button>
                ))}
              </motion.div>
            )}

            {/* Rating submitted */}
            {current.isRating && ratingSubmitted && (
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                className="flex flex-col items-center mt-5">
                <Sparkles size={28} className="text-amber-400 mb-2" />
                <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">Thank you! 🙏</p>
              </motion.div>
            )}

            {/* Buttons */}
            <div className="mt-5 space-y-2">
              {current.isRating ? (
                !ratingSubmitted && (
                  <>
                    <motion.button whileTap={{ scale: 0.98 }} onClick={submitRating}
                      disabled={rating === 0}
                      className={`w-full py-2.5 rounded-xl font-bold text-xs text-white bg-gradient-to-r ${current.gradient} shadow-xs disabled:opacity-40 flex items-center justify-center gap-1.5 cursor-pointer`}>
                      <Sparkles size={14} /> Submit Rating
                    </motion.button>
                    <button onClick={markDone}
                      className="w-full py-2 text-xs font-semibold text-[#6b6860] dark:text-[#a8a49c] hover:text-[#1a1917] dark:hover:text-white flex items-center justify-center gap-1 transition-colors">
                      {current.skip}
                    </button>
                  </>
                )
              ) : (
                <>
                  <motion.button whileTap={{ scale: 0.98 }} onClick={current.onAction}
                    className={`w-full py-2.5 rounded-xl font-bold text-xs text-white bg-gradient-to-r ${current.gradient} shadow-xs flex items-center justify-center gap-1.5 cursor-pointer`}>
                    {current.action} <ArrowRight size={14} />
                  </motion.button>
                  <button onClick={next}
                    className="w-full py-2 text-xs font-semibold text-[#6b6860] dark:text-[#a8a49c] hover:text-[#1a1917] dark:hover:text-white flex items-center justify-center gap-1 transition-colors">
                    {current.skip} <ChevronRight size={13} />
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
